import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardStackSection,
  Checkbox,
  CheckboxGroup,
  Divider,
  Heading,
  Stack,
  Text,
  TextField,
  TextLink,
  TrackingProvider,
} from "@origin-digital/ods-core";
import { IconCheckCircle } from "@origin-digital/ods-icons";
import { Form } from "../../components/Form";
import { pageRoutes } from "../../consts";
import { useSubmitJointAccountHolder } from "./useSubmitJointAccountHolder";
import { DateOfBirthField } from "./DateOfBirthField";
import { JointAccountHolderScreen } from ".";
import {
  EMPTY_DETAILS,
  fields,
  HolderDetails,
  normaliseDetails,
  validateDetails,
} from "./validation";

export interface JointAccount {
  accountNumber: string;
  serviceType: string;
  address: string;
}
type Step = "landing" | "selector" | "details" | "review" | "success" | "error";
export const JointAccountHolderFlow = ({
  accounts,
  hasHardship,
}: {
  accounts: JointAccount[];
  hasHardship: boolean;
}) => {
  const history = useHistory();
  const [step, setStep] = useState<Step>("landing");
  const [selected, setSelected] = useState<string[]>([]);
  const [details, setDetails] = useState<HolderDetails>(EMPTY_DETAILS);
  const [errors, setErrors] = useState<
    Partial<Record<keyof HolderDetails, string>>
  >({});
  const [selectionError, setSelectionError] = useState(false);
  const { submit: submitJointAccountHolder, isPending: pending } =
    useSubmitJointAccountHolder();
  const [successName, setSuccessName] = useState("");
  const submitted = useRef(false);
  const heading = useRef<HTMLDivElement>(null);
  const uniqueAccounts = accounts.filter(
    (account, index) =>
      accounts.findIndex(
        (other) => other.accountNumber === account.accountNumber
      ) === index
  );
  const validSelection =
    selected.length > 0 &&
    selected.every((number) =>
      uniqueAccounts.some((account) => account.accountNumber === number)
    );
  const close = () => {
    setDetails(EMPTY_DETAILS);
    setSelected([]);
    setSuccessName("");
    history.push(pageRoutes.base);
  };
  const navigate = (next: Step) => {
    setStep(next);
    setTimeout(() => heading.current?.focus(), 0);
  };
  const start = () => {
    if (uniqueAccounts.length === 1) {
      setSelected([uniqueAccounts[0].accountNumber]);
      navigate("details");
    } else navigate("selector");
  };
  const review = (event: React.FormEvent) => {
    const input =
      event.currentTarget.querySelector<HTMLInputElement>("#jah-dateOfBirth");
    const currentDetails = {
      ...details,
      dateOfBirth: input?.value.replace(/\u200e/g, "") || "",
    };
    const nextErrors = validateDetails(currentDetails);
    setErrors(nextErrors);
    const first = fields.find((field) => nextErrors[field.key]);
    if (first) {
      document.getElementById(`jah-${first.key}`)?.focus();
      return;
    }
    if (!validSelection) {
      navigate("selector");
      setSelectionError(true);
      return;
    }
    setDetails(normaliseDetails(currentDetails));
    navigate("review");
  };
  const submit = async () => {
    if (submitted.current) return;
    if (!validSelection) {
      navigate("selector");
      setSelectionError(true);
      return;
    }
    if (Object.keys(validateDetails(details)).length) {
      navigate("details");
      return;
    }
    submitted.current = true;
    try {
      await submitJointAccountHolder(selected, normaliseDetails(details));
      setSuccessName(`${details.firstName.trim()} ${details.lastName.trim()}`);
      navigate("success");
    } catch {
      navigate("error");
    } finally {
      setDetails(EMPTY_DETAILS);
    }
  };
  if (step === "landing")
    return (
      <TrackingProvider onTrackingCapture={() => undefined}>
        <JointAccountHolderScreen hasHardship={hasHardship} onStart={start} />
      </TrackingProvider>
    );
  const title = {
    selector: "First, which account(s)?",
    details: "Joint account holder’s details",
    review: "Review your details",
    success: "Success",
    error: "We couldn’t confirm your request",
  }[step];
  return (
    <TrackingProvider onTrackingCapture={() => undefined}>
      <CardStackSection>
        <Card padding="medium">
          <Stack space={["large", "xlarge"]}>
            {step === "success" && (
              <Box display="flex" justifyContent="center">
                <IconCheckCircle
                  color="positive"
                  size="medium"
                  variant="solid"
                />
              </Box>
            )}
            <Heading
              component="h1"
              variant={step === "review" || step === "success" ? "h4" : "h3"}
              align={step === "success" ? "center" : "left"}
            >
              <Box component="span" ref={heading} tabIndex={-1}>
                {title}
              </Box>
            </Heading>
            {step === "selector" && (
              <Form
                onSubmit={() => {
                  if (!validSelection) {
                    setSelectionError(true);
                    document.getElementById("jah-account-0")?.focus();
                    return;
                  }
                  setSelectionError(false);
                  navigate("details");
                }}
              >
                <Stack space="large">
                  <Text>
                    Which account do you want to add a joint account holder to?
                  </Text>
                  <CheckboxGroup
                    id="jah-accounts"
                    data-id="jah-accounts"
                    label="Accounts"
                    error={selectionError}
                    helperText={
                      selectionError
                        ? "Select at least one account to continue."
                        : undefined
                    }
                  >
                    <Stack space="medium">
                      {uniqueAccounts.map((account, index) => (
                        <Card
                          key={account.accountNumber}
                          outline
                          padding="small"
                        >
                          <Checkbox
                            id={`jah-account-${index}`}
                            data-id={`jah-account-${index}`}
                            checked={selected.includes(account.accountNumber)}
                            onChange={(_, checked) =>
                              setSelected((current) =>
                                checked
                                  ? [...current, account.accountNumber]
                                  : current.filter(
                                      (number) =>
                                        number !== account.accountNumber
                                    )
                              )
                            }
                            label={account.serviceType}
                            helperText={
                              <Stack space="xxsmall">
                                <Text variant="body-small">
                                  {account.accountNumber}
                                </Text>
                                <Text variant="body-small">
                                  {account.address}
                                </Text>
                              </Stack>
                            }
                          />
                        </Card>
                      ))}
                    </Stack>
                  </CheckboxGroup>
                  <Button
                    id="jah-continue"
                    data-id="jah-continue"
                    type="submit"
                  >
                    Continue
                  </Button>
                  <Heading component="h2" variant="h4">
                    Don’t see an account?
                  </Heading>
                  <Text>
                    If you want to add a joint account holder to any other
                    account such as your LPG and internet account,{" "}
                    <TextLink
                      id="jah-account-contact"
                      data-id="jah-account-contact"
                      href="tel:132468"
                    >
                      contact us
                    </TextLink>
                    .
                  </Text>
                </Stack>
              </Form>
            )}
            {step === "details" && (
              <Form onSubmit={review}>
                <Stack space="medium">
                  <Text>
                    Please provide details for the joint account holder.
                  </Text>
                  {fields.map(({ key, label, maxLength }) =>
                    key === "dateOfBirth" ? (
                      <DateOfBirthField
                        key={key}
                        value={details.dateOfBirth}
                        error={errors.dateOfBirth}
                      />
                    ) : (
                      <TextField
                        key={key}
                        id={`jah-${key}`}
                        data-id={`jah-${key}`}
                        label={label}
                        value={details[key]}
                        maxLength={maxLength}
                        error={!!errors[key]}
                        helperText={errors[key]}
                        onChange={(event) =>
                          setDetails((current) => ({
                            ...current,
                            [key]: event.target.value,
                          }))
                        }
                        onBlur={() =>
                          setErrors((current) => ({
                            ...current,
                            [key]: validateDetails(details)[key],
                          }))
                        }
                        type={key === "mobileNumber" ? "tel" : "text"}
                      />
                    )
                  )}
                  <Card
                    backgroundColor="cardBackgroundSecondary"
                    padding="small"
                  >
                    <Stack space="xsmall">
                      <Text weight="medium">
                        Want to add name onto your bills?
                      </Text>
                      <Text>
                        To have your Joint account holder’s name added to your
                        Origin bills, please{" "}
                        <TextLink
                          id="jah-bill-contact"
                          data-id="jah-bill-contact"
                          href="tel:132468"
                        >
                          call us
                        </TextLink>
                        .
                      </Text>
                    </Stack>
                  </Card>
                  <Button
                    id="jah-review"
                    data-id="jah-review"
                    type="submit"
                    fullWidth
                  >
                    Continue
                  </Button>
                </Stack>
              </Form>
            )}
            {step === "review" && (
              <Stack space="large">
                <Text>
                  Please let {details.firstName} know that you’re sharing their
                  information with us and tell them about our privacy and credit
                  reporting statements which are available at{" "}
                  <TextLink
                    id="jah-privacy"
                    data-id="jah-privacy"
                    href="https://www.originenergy.com.au/privacy"
                    target="_blank"
                  >
                    www.originenergy.com.au/privacy
                  </TextLink>
                </Text>
                <Text>
                  If you want to edit or remove the Joint Account holder please{" "}
                  <TextLink
                    id="jah-review-contact"
                    data-id="jah-review-contact"
                    href="tel:132468"
                  >
                    call us
                  </TextLink>
                  .
                </Text>
                <Stack space="medium" dividers>
                  {fields.map(({ key, label }) => (
                    <Stack key={key} space="xxsmall">
                      <Text variant="body-small">{label}</Text>
                      <Text wordBreak>{details[key]}</Text>
                    </Stack>
                  ))}
                </Stack>
                <Divider data-id="jah-review-email-divider" />
                <Heading component="h2" variant="h4">
                  Selected accounts
                </Heading>
                {uniqueAccounts
                  .filter((account) => selected.includes(account.accountNumber))
                  .map((account) => (
                    <Text key={account.accountNumber}>
                      {account.serviceType} · {account.accountNumber} ·{" "}
                      {account.address}
                    </Text>
                  ))}
                <Button
                  id="jah-submit"
                  data-id="jah-submit"
                  onClick={submit}
                  disabled={pending}
                  spinner={pending}
                  fullWidth
                >
                  Confirm
                </Button>
              </Stack>
            )}
            {step === "success" && (
              <Stack space="large">
                <Text data-id="jah-result" align="center">
                  We have recorded {successName}’s joint account holder details
                  on your selected accounts.
                </Text>
                <Button
                  id="jah-done"
                  data-id="jah-done"
                  fullWidth
                  onClick={close}
                >
                  Done
                </Button>
              </Stack>
            )}
            {step === "error" && (
              <Text data-id="jah-result">
                Your request may have been recorded on some accounts. Please{" "}
                <TextLink
                  id="jah-error-contact"
                  data-id="jah-error-contact"
                  href="tel:132468"
                >
                  call us
                </TextLink>{" "}
                to check before submitting another request.
              </Text>
            )}
          </Stack>
        </Card>
      </CardStackSection>
    </TrackingProvider>
  );
};
